/**
 * Док интерактивных сессий оценки Hermes — выезжающая справа панель.
 *
 * Несколько сессий живут одновременно: все iframe смонтированы, видна активная
 * (остальные display:none — диалог в них не сбрасывается). Мост postMessage с
 * обязательной проверкой event.origin. На hermes:done результат НЕ берётся из
 * браузера — фронт зовёт finalize, бэк перечитывает оценку и пишет поля.
 *
 * docs/modules/hermes-embed.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Drawer, Segmented, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import {
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
  ReloadOutlined,
  MessageOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { HermesEstimationMode } from '@lens/shared';
import { finalizeEmbedEstimate, fetchHermesPresets } from './api.ts';
import {
  useHermesSessions,
  type HermesSession,
  type HermesSessionStatus,
} from './HermesSessionsContext.tsx';
import { useThemeMode } from '../../shared/theme/ThemeProvider.tsx';

/** Событие для грида: запись пере­оценена интерактивно — пора обновить статус. */
export const HERMES_FINALIZED_EVENT = 'lens:hermes-finalized';
export interface HermesFinalizedDetail {
  itemId: string;
  spaceId: string;
  status: string | null;
}

const STATUS_META: Record<HermesSessionStatus, { text: string; color: string }> = {
  opening: { text: 'Открываем…', color: 'default' },
  ready: { text: 'Готова', color: 'blue' },
  working: { text: 'Считается', color: 'processing' },
  needs_input: { text: 'Нужен ответ', color: 'gold' },
  done: { text: 'Готово', color: 'green' },
  error: { text: 'Ошибка', color: 'red' },
  expired: { text: 'Истекла', color: 'orange' },
};

const MODE_OPTIONS: Array<{ value: HermesEstimationMode; label: string }> = [
  { value: 'auto', label: 'Авто' },
  { value: 'high_level', label: 'Высокоур.' },
  { value: 'detailed', label: 'Детал.' },
];

const MODE_DESC: Record<HermesEstimationMode, string> = {
  auto: 'Hermes сам выберет глубину под задачу',
  high_level: 'Быстро, крупными мазками — без деталей',
  detailed: 'Подробно, с разбивкой по ролям и шагам',
};

export function HermesSessionDock(): JSX.Element | null {
  const { sessions, open, activeId, expanded, drawerWidth, toggleExpanded, closeDrawer, setActive, closeSession } =
    useHermesSessions();

  const active = sessions.find((s) => s.id === activeId) ?? sessions[0];
  if (!active) return null;

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      placement="right"
      mask={false}
      width={drawerWidth}
      title={
        <Space size={6} align="center">
          <MessageOutlined />
          <span>Оценка трудоёмкости</span>
        </Space>
      }
      extra={
        <Space size={4}>
          <Tooltip title={expanded ? 'Свернуть ширину' : 'Развернуть'}>
            <Button
              type="text"
              size="small"
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={toggleExpanded}
            />
          </Tooltip>
        </Space>
      }
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Переключатель открытых сессий */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '8px 12px',
          overflowX: 'auto',
          borderBottom: '1px solid rgba(128,128,128,0.18)',
          flexShrink: 0,
        }}
      >
        {sessions.map((s) => {
          const meta = STATUS_META[s.status];
          const isActive = s.id === active.id;
          return (
            <span
              key={s.id}
              onClick={() => setActive(s.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 8px',
                borderRadius: 16,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: 12,
                border: isActive
                  ? '1px solid var(--ant-color-primary, #1677ff)'
                  : '1px solid rgba(128,128,128,0.25)',
                background: isActive ? 'rgba(22,119,255,0.08)' : 'transparent',
              }}
            >
              <Tag color={meta.color} style={{ margin: 0, padding: '0 4px', lineHeight: '16px' }}>
                {s.itemKey ?? '—'}
              </Tag>
              <CloseOutlined
                style={{ fontSize: 10, opacity: 0.6 }}
                onClick={(e) => {
                  e.stopPropagation();
                  closeSession(s.id);
                }}
              />
            </span>
          );
        })}
      </div>

      {/* Все сессии смонтированы; видна активная. */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {sessions.map((s) => (
          <div
            key={s.id}
            style={{
              position: 'absolute',
              inset: 0,
              display: s.id === active.id ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <SessionView session={s} />
          </div>
        ))}
      </div>
    </Drawer>
  );
}

function SessionView({ session }: { session: HermesSession }): JSX.Element {
  const { updateSession, reopenSession } = useHermesSessions();
  const { mode: themeMode } = useThemeMode();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const contextSentRef = useRef(false);
  const finalizingRef = useRef(false);
  const presetsFetchedRef = useRef(false);
  // Диалог стартует только после явного «Начать» — чтобы пользователь успел
  // выбрать формат оценки до отправки hermes:context. iframe монтируется тоже
  // только после старта.
  const [started, setStarted] = useState(false);

  const { id, spaceId, itemId, hermesOrigin, embedUrl, embedToken, taskDescription, mode, preset, presets, presetsStatus, status, error, expiresAt } =
    session;

  // Discovery пресетов: тянем список из бэка (GET .../hermes-presets), дефолт —
  // пресет с is_default (или первый). Пустой список/ошибка — селектор скрыт.
  useEffect(() => {
    // Гейт через ref, НЕ через presetsStatus в deps: смена статуса на 'loading'
    // внутри эффекта иначе перезапускала бы его → cleanup ставил cancelled=true
    // до резолва fetch → статус навсегда застревал в 'loading'.
    if (presetsFetchedRef.current) return;
    presetsFetchedRef.current = true;
    let cancelled = false;
    updateSession(id, { presetsStatus: 'loading' });
    fetchHermesPresets(spaceId, itemId)
      .then((list) => {
        if (cancelled) return;
        const def = list.find((p) => p.isDefault) ?? list[0];
        updateSession(id, { presets: list, presetsStatus: 'ready', preset: def?.id });
      })
      .catch(() => {
        if (!cancelled) updateSession(id, { presets: [], presetsStatus: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [id, spaceId, itemId, updateSession]);

  // Финализация по hermes:done — бэк перечитывает результат, не доверяя браузеру.
  const finalize = useCallback(
    async (taskId: string) => {
      if (finalizingRef.current) return;
      finalizingRef.current = true;
      updateSession(id, { taskId });
      try {
        const res = await finalizeEmbedEstimate(spaceId, itemId, taskId);
        updateSession(id, { status: 'done' });
        const detail: HermesFinalizedDetail = { itemId, spaceId, status: res.status };
        window.dispatchEvent(new CustomEvent(HERMES_FINALIZED_EVENT, { detail }));
      } catch (err) {
        updateSession(id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Не удалось записать результат оценки',
        });
      } finally {
        finalizingRef.current = false;
      }
    },
    [id, spaceId, itemId, updateSession],
  );

  // Мост postMessage. Слушаем только сообщения нашего iframe и нашего origin.
  useEffect(() => {
    if (!hermesOrigin) return;
    function onMessage(e: MessageEvent) {
      if (e.origin !== hermesOrigin) return;
      const win = iframeRef.current?.contentWindow;
      // Сообщение принимаем ТОЛЬКО от своего iframe. Раньше проверка была
      // мягкой (`win && e.source !== win`): у сессии без смонтированного
      // iframe (ещё не нажали «Начать оценку», либо сессия истекла/упала)
      // win === undefined, проверка пропускалась, и такая сессия ловила
      // hermes:done соседней сессии — результат чужого расчёта уезжал в её
      // запись. Так на проде оценки трёх задач перемешались.
      if (!win || e.source !== win) return;
      const m = (e.data ?? {}) as {
        type?: string;
        channel?: string;
        status?: string;
        height?: number;
        task_id?: string;
        message?: string;
      };
      switch (m.type) {
        case 'hermes:ready':
          if (!contextSentRef.current && win) {
            // Гермес принимает hermes:context только с тем же channel, который
            // сам прислал в hermes:ready, и только если каждое поле — строка
            // (ключ со значением undefined приводит к отбрасыванию сообщения
            // целиком). Поэтому channel возвращаем как есть, а preset кладём,
            // только когда он выбран.
            win.postMessage(
              {
                type: 'hermes:context',
                ...(m.channel ? { channel: m.channel } : {}),
                task_description: taskDescription ?? '',
                mode,
                ...(preset ? { preset } : {}),
                theme: themeMode,
              },
              hermesOrigin,
            );
            contextSentRef.current = true;
            updateSession(id, { status: 'working' });
          }
          break;
        case 'hermes:status':
          if (m.status === 'working') updateSession(id, { status: 'working' });
          else if (m.status === 'needs_input') updateSession(id, { status: 'needs_input' });
          else if (m.status === 'error') updateSession(id, { status: 'error' });
          break;
        case 'hermes:done':
          if (m.task_id) void finalize(m.task_id);
          break;
        case 'hermes:error':
          updateSession(id, { status: 'error', error: m.message ?? 'Ошибка в Hermes' });
          break;
        // hermes:resize намеренно игнорируем — iframe занимает 100% высоты панели,
        // содержимое скроллится внутри, окно всегда помещается.
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [hermesOrigin, taskDescription, mode, preset, themeMode, id, updateSession, finalize]);

  // Детект истечения embed-токена (TTL ~1ч).
  useEffect(() => {
    if (!expiresAt || status === 'done' || status === 'error' || status === 'expired') return;
    const ms = expiresAt - Date.now();
    if (ms <= 0) {
      updateSession(id, { status: 'expired' });
      return;
    }
    const t = setTimeout(() => updateSession(id, { status: 'expired' }), ms);
    return () => clearTimeout(t);
  }, [expiresAt, status, id, updateSession]);

  // Переоткрытие (expired/error → opening) сбрасывает старт и флаг контекста.
  useEffect(() => {
    if (status === 'opening') {
      contextSentRef.current = false;
      setStarted(false);
    }
  }, [status]);

  const meta = STATUS_META[status];
  // Заголовок записи: «КЛЮЧ · Название», но без дубля, если названия нет
  // (title упал в ключ) или оно совпадает с ключом.
  const recordLabel =
    session.itemKey && session.title && session.title !== session.itemKey
      ? `${session.itemKey} · ${session.title}`
      : session.title || session.itemKey || 'Запись';
  const modeLocked = started || ['working', 'needs_input', 'done'].includes(status);
  const showIframe = Boolean(embedUrl && embedToken && started && status !== 'error' && status !== 'expired');
  const showPreStart = Boolean(embedUrl && embedToken && !started && status !== 'opening' && status !== 'error' && status !== 'expired');

  return (
    <>
      {/* Шапка сессии: запись, статус, режим */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderBottom: '1px solid rgba(128,128,128,0.18)',
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography.Text strong ellipsis style={{ fontSize: 13 }}>
            {recordLabel}
          </Typography.Text>
          <Tag color={meta.color} style={{ width: 'fit-content', marginTop: 2 }}>
            {meta.text}
          </Tag>
        </div>
        <div style={{ marginLeft: 'auto', display: showPreStart ? 'none' : 'block' }}>
          <Segmented
            size="small"
            value={mode}
            disabled={modeLocked}
            onChange={(v) => updateSession(id, { mode: v as HermesEstimationMode })}
            options={MODE_OPTIONS}
          />
        </div>
      </div>

      {/* Тело: спиннер / ошибка / iframe */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {status === 'opening' && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin tip="Получаем сессию…" />
          </div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16 }}>
            <Alert
              type="error"
              showIcon
              message="Сессия оценки недоступна"
              description={error}
              action={
                <Button size="small" icon={<ReloadOutlined />} onClick={() => reopenSession(id)}>
                  Повторить
                </Button>
              }
            />
          </div>
        )}
        {status === 'expired' && (
          <div style={{ padding: 16 }}>
            <Alert
              type="warning"
              showIcon
              message="Сессия истекла"
              description="Токен доступа жил ~1 час и протух. Диалог на стороне Hermes не потерян — откройте сессию заново."
              action={
                <Button size="small" icon={<ReloadOutlined />} onClick={() => reopenSession(id)}>
                  Открыть заново
                </Button>
              }
            />
          </div>
        )}
        {showPreStart && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: 20,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 340,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 18,
                padding: '28px 24px',
                borderRadius: 16,
                background: 'var(--color-bg-container)',
                border: '1px solid var(--color-border)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                textAlign: 'center',
              }}
            >
              {/* Иконка-бейдж */}
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--color-primary-bg)',
                  color: 'var(--color-primary)',
                  fontSize: 28,
                }}
              >
                <MessageOutlined />
              </div>

              {/* Заголовок + запись + описание */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Интерактивная оценка
                </Typography.Title>
                <Typography.Text strong style={{ fontSize: 13, color: 'var(--color-primary)' }}>
                  {recordLabel}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
                  Hermes уточнит детали в диалоге и вернёт оценку трудоёмкости.
                </Typography.Text>
              </div>

              {/* Выбор формата */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Typography.Text
                  style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-secondary)' }}
                >
                  Формат оценки
                </Typography.Text>
                <Segmented
                  block
                  value={mode}
                  onChange={(v) => updateSession(id, { mode: v as HermesEstimationMode })}
                  options={MODE_OPTIONS}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12, minHeight: 18 }}>
                  {MODE_DESC[mode]}
                </Typography.Text>
              </div>

              {/* Пресет настроек — список приходит из discovery Hermes. */}
              {presetsStatus === 'loading' && (
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spin size="small" />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Загружаем пресеты…
                  </Typography.Text>
                </div>
              )}
              {presetsStatus === 'ready' && presets && presets.length > 0 && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Typography.Text
                    style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-secondary)' }}
                  >
                    Пресет настроек
                  </Typography.Text>
                  <Select
                    style={{ width: '100%' }}
                    value={preset}
                    onChange={(v) => updateSession(id, { preset: v })}
                    options={presets.map((p) => ({ value: p.id, label: p.name }))}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12, minHeight: 18 }}>
                    {presets.find((p) => p.id === preset)?.description ?? ''}
                  </Typography.Text>
                </div>
              )}

              {/* Старт */}
              <Button
                type="primary"
                size="large"
                block
                icon={<PlayCircleOutlined />}
                onClick={() => setStarted(true)}
              >
                Начать оценку
              </Button>
            </div>
          </div>
        )}
        {showIframe && embedUrl && embedToken && (
          <iframe
            ref={iframeRef}
            title={`Hermes · ${session.itemKey ?? session.title}`}
            src={`${embedUrl}?embed=${encodeURIComponent(embedToken)}`}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        )}
      </div>

      {/* Подвал: подсказка / статус записи */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderTop: '1px solid rgba(128,128,128,0.18)',
          flexShrink: 0,
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Ключ Hermes на бэкенде · результат запишет сервер
        </Typography.Text>
        {status === 'done' && (
          <Tag color="green" style={{ marginLeft: 'auto' }}>
            Результат записан в поля
          </Tag>
        )}
      </div>
    </>
  );
}
